import { forwardRef, useCallback } from "react";
import { MenuItem } from "@mui/material";
import { useSelector } from "react-redux";
import { selectPermissions } from "../store/redux/slices/Permission-slice";
import { selectAuthData } from "../store/redux/slices/Auth-slice";
import { usePagePermissions } from "./PermissionProvider";
import { isAppAdmin } from "./app-admin";

export const useCanFn = () => {
    const permissions = useSelector(selectPermissions);
    const authData = useSelector(selectAuthData);
    const roleCode = authData?.user?.role_code;

    return useCallback(
        (feature, action) => {
            if (isAppAdmin(roleCode)) return true;
            if (!feature) return true;
            if (!permissions) return false;
            return !!permissions[feature]?.includes(action);
        },
        [permissions, roleCode]
    );
};

export const useCan = (feature, action) => {
    const pagePerms = usePagePermissions();
    const can = useCanFn();
    return can(feature || pagePerms.feature, action);
};

export const useCanAny = (pairs = []) => {
    const can = useCanFn();
    return pairs.some(([feature, action]) => can(feature, action));
};

export const Can = ({ feature = null, action, children, fallback = null }) => {
    const allowed = useCan(feature, action);
    return allowed ? children : fallback;
};

export const PermMenuItem = forwardRef(
    ({ action = "view", feature = null, children, ...props }, ref) => {
        const allowed = useCan(feature, action);
        if (!allowed) return null;
        return (
            <MenuItem ref={ref} {...props}>
                {children}
            </MenuItem>
        );
    }
);
PermMenuItem.displayName = "PermMenuItem";

export default Can;
